/**
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 *
 */

import type {EditorThemeClasses} from 'lexical';

import './NEDTheme.css';

const theme: EditorThemeClasses = {
  blockCursor: 'NEDTheme__blockCursor',
  characterLimit: 'NEDTheme__characterLimit',
  code: 'NEDTheme__code',
  codeHighlight: {
    atrule: 'NEDTheme__tokenAttr',
    attr: 'NEDTheme__tokenAttr',
    boolean: 'NEDTheme__tokenProperty',
    builtin: 'NEDTheme__tokenSelector',
    cdata: 'NEDTheme__tokenComment',
    char: 'NEDTheme__tokenSelector',
    class: 'NEDTheme__tokenFunction',
    'class-name': 'NEDTheme__tokenFunction',
    comment: 'NEDTheme__tokenComment',
    constant: 'NEDTheme__tokenProperty',
    deleted: 'NEDTheme__tokenProperty',
    doctype: 'NEDTheme__tokenComment',
    entity: 'NEDTheme__tokenOperator',
    function: 'NEDTheme__tokenFunction',
    important: 'NEDTheme__tokenVariable',
    inserted: 'NEDTheme__tokenSelector',
    keyword: 'NEDTheme__tokenAttr',
    namespace: 'NEDTheme__tokenVariable',
    number: 'NEDTheme__tokenProperty',
    operator: 'NEDTheme__tokenOperator',
    prolog: 'NEDTheme__tokenComment',
    property: 'NEDTheme__tokenProperty',
    punctuation: 'NEDTheme__tokenPunctuation',
    regex: 'NEDTheme__tokenVariable',
    selector: 'NEDTheme__tokenSelector',
    string: 'NEDTheme__tokenSelector',
    symbol: 'NEDTheme__tokenProperty',
    tag: 'NEDTheme__tokenProperty',
    url: 'NEDTheme__tokenOperator',
    variable: 'NEDTheme__tokenVariable',
  },
  embedBlock: {
    base: 'NEDTheme__embedBlock',
    focus: 'NEDTheme__embedBlockFocus',
  },
  hashtag: 'NEDTheme__hashtag',
  heading: {
    h1: 'NEDTheme__h1',
    h2: 'NEDTheme__h2',
    h3: 'NEDTheme__h3',
    h4: 'NEDTheme__h4',
    h5: 'NEDTheme__h5',
    h6: 'NEDTheme__h6',
  },
  image: 'editor-image',
  indent: 'NEDTheme__indent',
  indentPortrait: 'NEDTheme__indentPortrait',
  inlineImage: 'inline-editor-image',
  layoutContainer: 'NEDTheme__layoutContainer',
  layoutItem: 'NEDTheme__layoutItem',
  link: 'NEDTheme__link',
  list: {
    listitem: 'NEDTheme__listItem',
    listitemChecked: 'NEDTheme__listItemChecked',
    listitemUnchecked: 'NEDTheme__listItemUnchecked',
    nested: {
      listitem: 'NEDTheme__nestedListItem',
    },
    olDepth: [
      'NEDTheme__ol1',
      'NEDTheme__ol2',
      'NEDTheme__ol3',
      'NEDTheme__ol4',
      'NEDTheme__ol5',
    ],
    ul: 'NEDTheme__ul',
  },
  ltr: 'NEDTheme__ltr',
  mark: 'NEDTheme__mark',
  markOverlap: 'NEDTheme__markOverlap',
  paragraph: 'NEDTheme__paragraph',
  quote: 'NEDTheme__quote',
  rtl: 'NEDTheme__rtl',
  table: 'NEDTheme__table',
  tableAddColumns: 'NEDTheme__tableAddColumns',
  tableAddRows: 'NEDTheme__tableAddRows',
  tableCell: 'NEDTheme__tableCell',
  tableCellActionButton: 'NEDTheme__tableCellActionButton',
  tableCellActionButtonContainer:
    'NEDTheme__tableCellActionButtonContainer',
  tableCellEditing: 'NEDTheme__tableCellEditing',
  tableCellHeader: 'NEDTheme__tableCellHeader',
  tableCellPrimarySelected: 'NEDTheme__tableCellPrimarySelected',
  tableCellResizer: 'NEDTheme__tableCellResizer',
  tableCellSelected: 'NEDTheme__tableCellSelected',
  tableCellSortedIndicator: 'NEDTheme__tableCellSortedIndicator',
  tableResizeRuler: 'NEDTheme__tableCellResizeRuler',
  tableSelected: 'NEDTheme__tableSelected',
  tableSelection: 'NEDTheme__tableSelection',
  text: {
    bold: 'NEDTheme__textBold',
    code: 'NEDTheme__textCode',
    italic: 'NEDTheme__textItalic',
    strikethrough: 'NEDTheme__textStrikethrough',
    subscript: 'NEDTheme__textSubscript',
    superscript: 'NEDTheme__textSuperscript',
    underline: 'NEDTheme__textUnderline',
    underlineStrikethrough: 'NEDTheme__textUnderlineStrikethrough',
  },
};

export default theme;
